import { CartStateData } from 'models';
import { attrLang } from './attrLang';
import { getOrderCost } from './getOrderCost';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

export const GA_CURRENCY = 'JPY';

/**
 * Query params stripped from `page_location` before it is sent to GA4.
 *
 * `d` changes on every visit and would fragment the page report (requested by the client).
 * `cid` and `token` carry the session credentials and must never leave the app.
 */
const EXCLUDED_QUERY_PARAMS = ['d', 'cid', 'token'];

let initialized = false;

export function isGaEnabled(): boolean {
  return GA_MEASUREMENT_ID !== '';
}

/**
 * Injects the gtag.js script and configures the measurement id.
 *
 * `send_page_view: false` is required: the automatic page_view would double-count
 * the first screen, which is already covered by the navigation hook.
 */
export function initGa(): void {
  if (!isGaEnabled() || initialized) return;

  initialized = true;

  window.dataLayer = window.dataLayer || [];

  // gtag.js requires the real `arguments` object on dataLayer — pushing a rest-param
  // array instead breaks the tag, so this cannot be an arrow function.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');

  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Builds the current location with the excluded query params removed.
 */
export function sanitizeLocation(): { page_location: string; page_path: string } {
  const url = new URL(window.location.href);

  EXCLUDED_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));

  return {
    page_location: url.href,
    page_path: `${url.pathname}${url.search}`,
  };
}

export function trackPageView(): void {
  if (!isGaEnabled() || !window.gtag) return;

  window.gtag('event', 'page_view', sanitizeLocation());
}

export interface GaPurchaseItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

/**
 * Flattens the cart into GA4 ecommerce items.
 *
 * Portions of the same dish can differ in price (different options), so each
 * distinct price is reported as its own line with an aggregated quantity.
 * Prices come from `getOrderCost` — the same helper `selectTotalAmount` uses,
 * so the item prices always add up to the reported `value`.
 */
export function buildPurchaseItems(cartState: CartStateData): GaPurchaseItem[] {
  const items: GaPurchaseItem[] = [];

  Object.values(cartState).forEach((dishValue) => {
    const dishInfo = dishValue.menuItemInfo;
    const byPrice: Record<string, GaPurchaseItem> = {};

    dishValue.state.forEach((portionState) => {
      const price = getOrderCost(portionState, dishInfo);
      const existing = byPrice[price];

      if (existing) {
        existing.quantity += 1;
        return;
      }

      byPrice[price] = {
        item_id: dishInfo.poscd,
        item_name: attrLang(dishInfo, 'lang'),
        price,
        quantity: 1,
      };
    });

    items.push(...Object.values(byPrice));
  });

  return items;
}

export interface TrackPurchaseParams {
  transactionId: string;
  value: number;
  items: GaPurchaseItem[];
}

/**
 * Sends one `purchase` event per confirmed order.
 *
 * `transaction_id` is the POS order number, so GA4 de-duplicates the event
 * even if it were somehow sent twice — guaranteeing 1 order = 1 count.
 */
export function trackPurchase({ transactionId, value, items }: TrackPurchaseParams): void {
  if (!isGaEnabled() || !window.gtag) return;

  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value,
    currency: GA_CURRENCY,
    items,
  });
}
